/**
 * This module provides a few different helpers to populate the `getSchemaOwner` field in
 * client/samples/SchemaStarsUtil.tsx:SchemaMenuOptions.
 */

import {OperationVariables, QueryDataOptions} from '@apollo/client';
import React, {useRef} from 'react';
import {useCallback, useMemo} from 'react';
import _ from 'underscore';

import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import EntityStore from 'stores/EntityStore';

interface SchemaOwnerQueryComponentProps<TData, TVariables extends OperationVariables = OperationVariables>
  extends Omit<QueryDataOptions<TData, TVariables>, 'children'> {
  children: (props: {getSchemaOwner: GetSchemaOwner}) => JSX.Element;
  getIdOwnerTuples: GetIdOwnerTuples<TData>;
}

type GetSchemaOwner = (schema: {id: string}) => string | undefined;
type GetIdOwnerTuples<TData> = (data: TData) => Array<{id: string; owner: string}>;
type Params<TData> = ({result: SkippableBenchlingQueryResult<TData>} | {data: TData}) & {
  getIdOwnerTuples: GetIdOwnerTuples<TData>;
};

/**
 * Given the result or successful data of a GraphQL query, as well as a function to enumerate schemas with
 * owners, return a function that does a lookup from schema id to owner name.
 *
 * @param param.data - GraphQL query result data from a successful query.
 * @param param.result - GraphQL query result that may be in any state. The returned function will return
 *   undefined if the state is not SUCCESS.
 * @param params.getIdOwnerTuples - A function that takes the GraphQL query result and returns a list of
 *   `{id: schemaId, owner: orgName}` objects. This function should not functionally change between renders.
 *   For convenience, an anonymous function can be used, but again, it is assumed that the function remains
 *   functionally identical.
 */
export function useGetSchemaOwnerFromQuery<TData>(params: Params<TData>): GetSchemaOwner {
  const data =
    // eslint-disable-next-line benchlint/no-in-operator
    'data' in params
      ? params.data
      : params.result.type === QueryResultType.SUCCESS
        ? params.result.data
        : null;
  // We use a ref so that we can ignore changes to it in useMemo.
  const getIdOwnerTuplesRef = useRef(params.getIdOwnerTuples);
  const nameById = useMemo(
    () =>
      data
        ? _.indexBy(getIdOwnerTuplesRef.current(data), (tuple: {id: string; owner: string}) => tuple.id)
        : {},
    [data, getIdOwnerTuplesRef]
  );
  const getSchemaOwner = useCallback(
    (schema: {id: string}): string | undefined => nameById[schema.id]?.owner,
    [nameById]
  );
  return getSchemaOwner;
}

/**
 * Given the result or successful data of a GraphQL query, as well as a function to enumerate schemas with
 * owners, expose a function that does a lookup from schema id to owner name, as a render prop.
 */
export function WithGetSchemaOwner<TData>(
  props: Params<TData> & {children: (props: {getSchemaOwner: GetSchemaOwner}) => JSX.Element}
): JSX.Element {
  const getSchemaOwner = useGetSchemaOwnerFromQuery(props);
  return props.children({getSchemaOwner});
}

export function shouldSkipOwnerQuery(): boolean {
  const user: EntityStoreUser | null = EntityStore.currentUser();
  // If the user is not in multiple organizations, we don't expect there to be any schemas with the same name.
  const userInMultipleOrgs = user != null && user.organizationIds != null && user.organizationIds.length > 1;
  return !userInMultipleOrgs;
}

/**
 * A helper component to assist in specifying the {@link SchemaMenuOptions.getSchemaOwner} used in {@link getSchemaSearchableMenuOptionDatas}
 *
 * NOTE: If you already have a GraphQL query for your schemas, you should just update the query to fetch
 * the owner information and then use {@link WithGetSchemaOwner} or {@link useGetSchemaOwnerFromQuery}
 * directly.
 *
 * @param {DocumentNode} props.query - a GraphQL query
 * @param props.getIdOwnerTuples - a function that transforms the data returned by the query
 *  into an array of {id, name} objects.
 *
 * @example
 *  const MY_SCHEMA_OWNER_QUERY: DocumentNode = gql\`
 *    query MySchemaOwnerQuery {
 *      foobarSchemas { id, organization { id, name } }
 *    }
 *  `;
 *  function MySchemaSelector({schemas}: {schemas: Array<Schema>}): JSX.Element {
 *    return (
 *      <SchemaOwnerQuery<MySchemaOwnerQuery>
 *        query={MY_SCHEMA_OWNER_QUERY}
 *        getIdOwnerTuples={(data) => data.foobarSchemas.map(
 *          ({id, organization}) => ({id, owner: organization.name})
 *        )}
 *      >
 *        {({getSchemaOwner}) => (
 *          <SchemaSelector
 *            ...
 *            menuOptionDatas={
 *              getSchemaSearchableMenuOptionDatas(schemas, [], {getSchemaOwner})
 *            }
 *            schemas={schemas}
 *          />
 *        )}
 *      </SchemaOwnerQuery>
 *  }
 *
 */
export function SchemaOwnerQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  props: SchemaOwnerQueryComponentProps<TData, TVariables>
): JSX.Element {
  const result = useSkippableBenchlingQuery<TData, TVariables>(props.query, {
    skip: shouldSkipOwnerQuery(),
  });
  return (
    <WithGetSchemaOwner result={result} getIdOwnerTuples={props.getIdOwnerTuples}>
      {props.children}
    </WithGetSchemaOwner>
  );
}

/**
 * Given a list of schemas and a function to look up the owner, return a function that will conditionally
 * return the owner if there are two or more schemas with that same name.
 */
export function getSchemaOptionDataMetadata<T extends {id: string; name: string}>(
  schemas: Array<T>,
  getSchemaOwner: (schema: T) => string | undefined
): (schema: T) => string | undefined {
  const schemasByName = _.countBy(schemas, (schema: T) => schema.name);

  return (schema: T): string | undefined => {
    if (schemasByName[schema.name] > 1) {
      return getSchemaOwner(schema);
    }
    return undefined;
  };
}
